<template>
  <div class="bg-blue-primary h-51x d-flex flex-row align-items-center">
    <div class="previous p-4">
      <a
        class="text-light text-decoration-none d-flex text-start align-items-center"
        href="#"
        @click="router.back()"
      >
        <img
          alt="My Logo"
          class="logo__image mr-5 text-light"
          src="@/assets/images/icons/angle-left.svg"
        />
        <span class="text-light ps-2 size-12 lh-12">FAQ</span>
      </a>
    </div>
  </div>
  <div class="container d-flex justify-content-center">
    <div class="faq-container">
      <div
        v-for="(category, categoryIndex) in faqCategories"
        :key="categoryIndex"
        class="faq-category"
      >
        <div class="faq-wrapper">
          <div class="faq-item">
            <button
              class="faq-category-button"
              :class="{ 'is-collapsed': activeCategoryIndex !== categoryIndex }"
              @click="toggleCategory(categoryIndex)"
            >
              {{ category.title }}
              <i class="faq-toggle-icon"></i>
            </button>

            <div v-show="activeCategoryIndex === categoryIndex" class="faq-category-content">
              <div v-for="(faq, faqIndex) in category.faqs" :key="faqIndex">
                <button
                  class="faq-question-button"
                  :class="{
                    'is-collapsed': !isQuestionActive(categoryIndex, faqIndex),
                  }"
                  @click="toggleQuestion(categoryIndex, faqIndex)"
                >
                  <img src="@/assets/images/icons/q-char-contained.svg" class="faq-question-icon" />
                  <span class="flex-grow-1">{{ faq.question }}</span>
                  <i class="faq-toggle-icon"></i>
                </button>
                <div v-show="isQuestionActive(categoryIndex, faqIndex)" class="faq-answer">
                  <img src="@/assets/images/icons/a-char.svg" class="me-2" />
                  <SanitizedHtml :html="faq.answer" class="answer-content" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { BASE_URL } from '@/configs';
import faqData from '@/data/faq.json';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Collapse from 'bootstrap/js/dist/collapse';
import SanitizedHtml from '@/components/common/SanitizedHtml.vue';

// 型定義
interface FAQ {
  question: string;
  answer: string;
}

interface Category {
  title: string;
  faqs: FAQ[];
}

interface ActiveQuestions {
  [key: string]: number | null;
}

// 定数
const CATEGORY_KEY_PREFIX = 'category_';

// コンポーネントのstate
const router = useRouter();
const activeCategoryIndex = ref<number | null>(null);
const activeQuestions = ref<ActiveQuestions>({});

// データの加工
const faqCategories = faqData.categories.map((category: Category) => ({
  ...category,
  faqs: category.faqs.map((faq: FAQ) => ({
    ...faq,
    answer: faq.answer.replace(/\${BASE_URL}/g, BASE_URL),
  })),
}));

// ユーティリティ関数
const getCategoryKey = (categoryIndex: number): string => {
  return `${CATEGORY_KEY_PREFIX}${categoryIndex}`;
};

const resetCategoryQuestions = (categoryIndex: number): void => {
  const key = getCategoryKey(categoryIndex);
  activeQuestions.value[key] = null;
};

// アコーディオン制御関数
const toggleCategory = (categoryIndex: number): void => {
  if (activeCategoryIndex.value === categoryIndex) {
    // 現在のカテゴリーを閉じる
    activeCategoryIndex.value = null;
    resetCategoryQuestions(categoryIndex);
  } else {
    // 別のカテゴリーを開く
    if (activeCategoryIndex.value !== null) {
      resetCategoryQuestions(activeCategoryIndex.value);
    }
    activeCategoryIndex.value = categoryIndex;
  }
};

const toggleQuestion = (categoryIndex: number, questionIndex: number): void => {
  const key = getCategoryKey(categoryIndex);
  activeQuestions.value[key] = activeQuestions.value[key] === questionIndex ? null : questionIndex;
};

const isQuestionActive = (categoryIndex: number, questionIndex: number): boolean => {
  const key = getCategoryKey(categoryIndex);
  return activeQuestions.value[key] === questionIndex;
};

onMounted(() => {
  // ドロップダウンの初期化
  const dropdownElementList = document.querySelectorAll('[data-bs-toggle="dropdown"]');
  Array.from(dropdownElementList).forEach((dropdownToggleEl) => {
    new Dropdown(dropdownToggleEl);
  });

  // アコーディオンの初期化
  const collapseElementList = document.querySelectorAll('[data-bs-toggle="collapse"]');
  Array.from(collapseElementList).forEach((collapseToggleEl) => {
    new Collapse(collapseToggleEl);
  });
});
</script>

<style lang="scss" scoped>
.faq {
  &-container {
    width: 100%;
    max-width: 960px;
    margin: 2rem auto 3rem;
    display: grid;
    gap: 1rem;
  }
  &-category {
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  &-category-content {
    padding: 0.5rem 1.5em;
    display: grid;
    gap: 0.5rem;
  }
  &-question {
    &-button {
      padding: 1.2em;
      background-color: #{rgba($primary, 0.05)};
      width: 100%;
      display: flex;
      align-items: center;
      gap: 16px;
      border: none;
      border-radius: 0.5rem;
      transition: background-color 0.2s ease;
      text-align: left;

      &:hover {
        background-color: #{rgba($primary, 0.1)};
      }

      &.is-collapsed {
        .faq-toggle-icon {
          transform: rotate(0deg);
        }
      }

      &:not(.is-collapsed) {
        .faq-toggle-icon {
          background-image: $accordion-button-active-icon;
          transform: rotate(180deg);
        }
      }
    }
    &-icon {
      width: 24px;
      height: 24px;
    }
  }
  &-toggle-icon {
    width: 1rem;
    height: 1rem;
    background-size: 1rem;
    background-image: $accordion-button-icon;
    transition: transform 0.2s ease-in-out;
    background-repeat: no-repeat;
    background-position: center;
  }
  &-category-button {
    box-shadow: none;
    padding: 1em 1.5em;
    color: $black-02;
    font-weight: 600;
    transition: color 0.2s ease;
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border-radius: 0.5rem;
    width: 100%;
    text-align: left;
    &.is-collapsed {
      .faq-toggle-icon {
        transform: rotate(0deg);
      }
    }
    &:not(.is-collapsed) {
      .faq-toggle-icon {
        background-image: $accordion-button-active-icon;
        transform: rotate(180deg);
      }
    }
    .faq-toggle-icon {
      width: 1rem;
      height: 1rem;
      background-size: 1rem;
      background-image: $accordion-button-icon;
      transition: transform 0.2s ease-in-out;
      background-repeat: no-repeat;
      background-position: center;
    }
  }
  &-item {
    padding: 0.5em 0;
    border-radius: 0.5em;
    &:nth-child(1) {
      border-top-left-radius: 0;
      border-top-right-radius: 0;

      @media screen and (min-width: $layout-md) {
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
      }
    }

    &:last-child {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: 0;

      @media screen and (min-width: $layout-md) {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
      }
    }
  }
  &-answer {
    padding: 1.5em 1.5em 1.5em 3em;
    text-indent: -1.5em;

    .answer-content {
      display: inline;

      :deep(a) {
        color: $primary;
        text-decoration: underline;

        &:hover {
          opacity: 0.8;
        }
      }
    }
  }
}
</style>
